import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useTheme,
  MAP_TYPES,
  MAP_TYPE_LABELS,
  getRegionDefinitions,
  type MapType,
  type ThemeContextValue,
} from '@course-authoring/shared-types';
import { useCreateMapVersion } from '../api/mapApi';
import { getRememberedListPath } from '../hooks/useListPathMemory';

type ThemeTokens = ThemeContextValue['tokens'];

export function CreateMapPage() {
  const navigate = useNavigate();
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const [title, setTitle] = useState('');
  const [mapType, setMapType] = useState<MapType | ''>('');
  const [error, setError] = useState<string | null>(null);

  const { mutate: createMap, isPending: isCreating } = useCreateMapVersion({
    onSuccess: (data) => {
      navigate(`/maps/${data.id}`);
    },
    onError: (err) => {
      setError(err.message || 'Failed to create map');
    },
  });

  const handleSubmit = useCallback(() => {
    if (isCreating) return;
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!mapType) {
      setError('Map type is required');
      return;
    }
    setError(null);
    const regionDefs = getRegionDefinitions(mapType);
    createMap({
      mapType,
      title: title.trim(),
      regions: regionDefs.map((d) => ({ regionKey: d.regionKey, name: d.name })),
    });
  }, [isCreating, title, mapType, createMap]);

  const handleCancel = useCallback(() => {
    navigate(getRememberedListPath('/maps'));
  }, [navigate]);

  const canSubmit = Boolean(title.trim()) && Boolean(mapType) && !isCreating;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h2 style={styles.heading}>New Map</h2>

        <label style={styles.fieldLabel}>
          Title
          <input
            type="text"
            value={title}
            onChange={(e) => { setTitle(e.target.value); setError(null); }}
            placeholder="e.g., US Harassment Laws"
            style={styles.textInput}
            autoFocus
          />
        </label>

        <label style={styles.fieldLabel}>
          Map Type
          <select
            value={mapType}
            onChange={(e) => { setMapType(e.target.value as MapType | ''); setError(null); }}
            style={styles.select}
          >
            <option value="">Select a map type...</option>
            {MAP_TYPES.map((t) => (
              <option key={t} value={t}>
                {MAP_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.actions}>
          <button type="button" onClick={handleCancel} style={styles.cancelButton}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...styles.submitButton,
              ...(!canSubmit ? styles.submitButtonDisabled : {}),
            }}
          >
            {isCreating ? 'Creating...' : 'Create Map'}
          </button>
        </div>
      </div>
    </div>
  );
}

function createStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    page: {
      display: 'flex',
      justifyContent: 'center',
      padding: tokens.spacing.xl,
      height: '100%',
      overflowY: 'auto',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      width: '100%',
      maxWidth: '520px',
    },
    heading: {
      margin: 0,
      fontSize: '18px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    fieldLabel: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    textInput: {
      height: '38px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
      boxSizing: 'border-box',
    },
    select: {
      height: '38px',
      padding: `0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      outline: 'none',
    },
    error: {
      margin: 0,
      fontSize: '14px',
      color: tokens.ui.errorText,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: tokens.spacing.md,
      marginTop: tokens.spacing.sm,
    },
    cancelButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
    },
    submitButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.onBrand,
      backgroundColor: tokens.brand.primary,
      border: 'none',
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      boxShadow: tokens.shadow.sm,
    },
    submitButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
}
